import React, { useEffect, useRef, useState } from 'react'
import { Download } from "lucide-react"
import CommaConvert from '../../utils/CommaConvert';
import DateConversion from '../../utils/DateConversion';
import { useCurrencySymbol } from '../../utils/currencyUtils';
import { Document, Packer, Paragraph, Table, TableCell, TableRow, TextRun } from "docx";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { useSelector } from 'react-redux';
import { usePdfExport } from '../../utils/usePdfExport';

const DownloadReport = ({ tableLeadReports }) => {

    const currency = useCurrencySymbol();
    const { formatDateByCountry } = DateConversion();
    const USAComma = CommaConvert();
    const { downloadPDF } = usePdfExport();

    console.log("tableLeadReports", tableLeadReports)
    const downloadReportTitle = useSelector((state) => state.page.downloadReportTitle);

    const handleDownload = (event) => {
        const value = event.target.value;
        if (value === "pdf") {
            downloadPDF({
                tableLeadReports,
                currency,
                downloadReportTitle,
                formatDateByCountry,
                USAComma
            });
        } else if (value === "word") {
            downloadWord();
        } else if (value === "excel") {
            downloadExcel();
        }
        setIsDropdownVisible(false);
    };

    const potentialStatus = [{}, {}]


    const downloadWord = () => {
        const now = new Date();
        const day = now.getDate();
        const month = now.toLocaleString("en-US", { month: "short" }); // Dec
        const year = now.getFullYear();


        const fileName = `${downloadReportTitle}_${day}_${month}_${year}.docx`;

        const safeText = (value) => (value === null || value === undefined ? " " : String(value));
        const docxParagraphFromText = (value) =>
            new Paragraph({
                children: [new TextRun(safeText(value))],
            });
        const leadRows = (tableLeadReports || []).filter((row) => row != null);

        const headerRow = new TableRow({
            children: [
                new TableCell({
                    children: [docxParagraphFromText("Sr.No")],
                }),
                new TableCell({
                    children: [docxParagraphFromText("Lead Date")],
                }),
                new TableCell({
                    children: [docxParagraphFromText("Client Name")],
                }),
                new TableCell({
                    children: [docxParagraphFromText("Project Name")],
                }),
                new TableCell({
                    children: [docxParagraphFromText("Lead Type")],
                }),
                new TableCell({
                    children: [docxParagraphFromText(`Quoted Value ${currency}`)],
                }),
                new TableCell({
                    children: [docxParagraphFromText("Source")],
                }),
                new TableCell({
                    children: [docxParagraphFromText("Status")],
                }),
                new TableCell({
                    children: [docxParagraphFromText("Group")],
                }),
                new TableCell({
                    children: [docxParagraphFromText("Follow-up Date")],
                }),
            ],
        });

        const dataRows = leadRows.map((LeadReport, index) => {
            return new TableRow({
                children: [
                    new TableCell({
                        children: [docxParagraphFromText(`${index + 1}`)],
                    }),
                    new TableCell({
                        children: [
                            docxParagraphFromText(
                                LeadReport.LeadDate ? formatDateByCountry(LeadReport.LeadDate) : " "
                            ),
                        ],
                    }),
                    new TableCell({
                        children: [docxParagraphFromText(LeadReport.ClientName)],
                    }),
                    new TableCell({
                        children: [docxParagraphFromText(LeadReport.ProjectName)],
                    }),
                    new TableCell({
                        children: [docxParagraphFromText(LeadReport.LeadType)],
                    }),
                    new TableCell({
                        children: [
                            docxParagraphFromText(
                                `${currency}${USAComma(LeadReport.QuotedValue)}`
                            ),
                        ],
                    }),
                    new TableCell({
                        children: [docxParagraphFromText(LeadReport.Source)],
                    }),
                    new TableCell({
                        children: [docxParagraphFromText(LeadReport.Status)],
                    }),
                    new TableCell({
                        children: [docxParagraphFromText(LeadReport.AssignedGroup)],
                    }),
                    new TableCell({
                        children: [
                            docxParagraphFromText(
                                LeadReport.Status === "Won" || LeadReport.Status === "Lost"
                                    ? " "
                                    : formatDateByCountry(LeadReport.FollowupDate)
                            ),
                        ],
                    }),
                ],
            });
        });

        const doc = new Document({
            sections: [
                {
                    properties: {},
                    children: [
                        new Table({
                            rows: [headerRow, ...dataRows],
                        }),
                    ],
                },
            ],
        });

        Packer.toBlob(doc).then((blob) => {
            saveAs(blob, fileName);
        });
    };

    const downloadExcel = () => {
        const sortedLeads = [...tableLeadReports].sort(
            (a, b) => new Date(a.LeadDate) - new Date(b.LeadDate)
        );

        const now = new Date();
        const day = now.getDate();
        const month = now.toLocaleString("en-US", { month: "short" });
        const year = now.getFullYear();
        const fileName = `${downloadReportTitle}_${day}_${month}_${year}.xlsx`;

        const ws = XLSX.utils.json_to_sheet(
            sortedLeads.map((LeadReport, index) => ({
                "Sr. No": index + 1,
                "Lead Date": LeadReport.LeadDate
                    ? formatDateByCountry(LeadReport.LeadDate)
                    : " ",
                "Client Name": LeadReport.ClientName,
                "Project Name": LeadReport.ProjectName,
                "Lead Type": LeadReport.LeadType,

                // 👉 store only number, not ₹/$
                [`Quoted Value (${currency})`]: Number(LeadReport.QuotedValue),

                "Source": LeadReport.Source,
                "Status": LeadReport.Status,
                "Group": LeadReport.AssignedGroup,
                "Follow-up Date":
                    ["Won", "Lost"].includes(LeadReport.Status)
                        ? " "
                        : formatDateByCountry(LeadReport.FollowupDate),
            }))
        );

        // 👉 Apply currency formatting to column F
        Object.keys(ws).forEach((cell) => {
            if (cell.startsWith("F") && cell !== "F1") {
                ws[cell].z = `${currency} #,##0.00`;
            }
        });

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Lead Reports");

        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const excelFile = new Blob([excelBuffer], {
            type: "application/octet-stream",
        });

        saveAs(excelFile, fileName);
    };


    const [isDropdownVisible, setIsDropdownVisible] = useState(false);

    const toggleDropdown = () => {
        setIsDropdownVisible(!isDropdownVisible); // Toggle dropdown visibility
    };

    const popupRef = useRef(null);

    const handleClickOutside = (event) => {
        if (popupRef.current && !popupRef.current.contains(event.target)) {
            setIsDropdownVisible(false); // Close the popup if clicked outside
        }
    };

    useEffect(() => {
        if (isDropdownVisible) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isDropdownVisible]);

    return (
        <div>
            <div className=" relative ">
                <div ref={popupRef} className={`flex ${potentialStatus.length === 0 ? 'opacity-50 pointer-events-none' : ''}`}>
                    <Download
                        onClick={toggleDropdown}
                        className="cursor-pointer"
                    // fontSize="4rem"
                    />

                    {isDropdownVisible && (
                        <div

                            className="absolute top-full -left-7 shadow-lg z-50 bg-white text-base"
                        >
                            <div
                                className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                                onClick={() =>
                                    handleDownload({ target: { value: "pdf" } })
                                }
                            >
                                PDF
                            </div>
                            <div
                                className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                                onClick={() =>
                                    handleDownload({ target: { value: "excel" } })
                                }
                            >
                                Excel
                            </div>
                            <div
                                className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                                onClick={() =>
                                    handleDownload({ target: { value: "word" } })
                                }
                            >
                                Word
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default DownloadReport
